import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ClearIcon from '@mui/icons-material/Clear'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import React from 'react'
import { Link } from 'react-router-dom'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import PolicyDrawer from '../../policies/drawer/policy-drawer'
import { formatCount, RamoMark } from '../common/automation-common'
import { useAutomationViewModel } from './automation-view-model'
import type { DetailKind } from './automation-view-model'

const KIND_STYLES: Record<DetailKind, { label: string, className: string, icon: React.ReactNode }> = {
    ok: { label: 'Importada', className: 'border-l-k-ok', icon: <CheckRoundedIcon className="text-k-ok" style={{ fontSize: 20 }} /> },
    review: { label: 'Por validar', className: 'border-l-tone-orange-ink', icon: <WarningRoundedIcon className="text-tone-orange-ink" style={{ fontSize: 20 }} /> },
    error: { label: 'Error', className: 'border-l-k-danger', icon: <ClearIcon className="text-k-danger" style={{ fontSize: 20 }} /> },
    pending: { label: 'En proceso', className: 'border-l-k-muted-2', icon: <AccessTimeIcon className="text-k-muted" style={{ fontSize: 20 }} /> },
}

const AutomationView = () => {
    const model = useAutomationViewModel()

    return (
        <KarteraAdminTemplate loading={false} title="Resultado del proceso">
            <PolicyDrawer polizaId={model.detailId} onClose={model.closePoliza} />

            <div className="mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Resultado del proceso"
                    description="Importación de pólizas desde el portal de la aseguradora."
                    backTo={model.backTo}
                    backLabel="Automatizaciones"
                />

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <section className={`k-card ${model.loading ? 'opacity-60' : ''}`}>
                    <h2 className="k-card-title">Resumen</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Aseguradora</span>
                            <span className="text-sm font-bold text-k-ink">{model.summary?.aseguradora || '—'}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Fecha</span>
                            <span className="text-sm font-semibold tabular-nums text-k-ink">{model.summary?.fecha || '—'}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Pólizas</span>
                            <span className="text-sm font-bold tabular-nums text-k-ink">{formatCount(model.summary?.total || model.totalItems)}</span>
                        </div>
                    </div>
                    {model.rows.length > 0 && (
                        <div className="flex flex-wrap gap-2 text-xs font-bold">
                            <span className="rounded-full bg-k-ok-soft px-2.5 py-1 text-k-ok">{formatCount(model.counts.ok)} importadas</span>
                            <span className="rounded-full bg-tone-orange px-2.5 py-1 text-tone-orange-ink">{formatCount(model.counts.review)} por validar</span>
                            <span className="rounded-full bg-k-danger-soft px-2.5 py-1 text-k-danger-ink">{formatCount(model.counts.error)} con error</span>
                            <span className="rounded-full bg-k-line-soft px-2.5 py-1 text-k-ink-2">{formatCount(model.counts.pending)} en proceso</span>
                            <span className="self-center font-normal text-k-muted">en esta página</span>
                        </div>
                    )}
                </section>

                {model.loading && (
                    <div className="flex flex-col gap-2" aria-busy="true">
                        {[1, 2, 3].map((row) => <div key={row} className="h-[84px] animate-pulse rounded-[14px] bg-k-line-soft" />)}
                    </div>
                )}

                {!model.loading && model.rows.length === 0 && !model.error && (
                    <section className="k-card">
                        <p className="m-0 text-sm text-k-muted">No hay detalle disponible para este proceso.</p>
                    </section>
                )}

                {!model.loading && model.rows.length > 0 && (
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                        {model.rows.map((row) => {
                            const style = KIND_STYLES[row.kind]
                            return (
                                <li key={row.id} className={`flex items-start gap-3 rounded-[14px] border border-l-4 border-k-line bg-k-surface px-4 py-3 ${style.className}`}>
                                    {row.ramo ? <RamoMark ramo={row.ramo} boxed /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-k-line-soft">{style.icon}</span>}
                                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <span className="flex flex-wrap items-center gap-2">
                                            {row.polizaId ? (
                                                <button type="button" className="border-0 bg-transparent p-0 text-sm font-bold text-k-primary-ink hover:underline" onClick={() => model.openPoliza(row.polizaId as number)}>
                                                    Póliza {row.noPoliza}
                                                </button>
                                            ) : <span className="text-sm font-bold text-k-ink">Póliza {row.noPoliza}</span>}
                                            {row.ramo && <span className="text-xs text-k-muted">{row.ramo}</span>}
                                        </span>
                                        {row.cliente && (
                                            <span className="text-xs text-k-ink-2">
                                                Contratante:{' '}
                                                {row.clienteId
                                                    ? <Link to={model.clientePath(row.clienteId)} className="font-semibold text-k-primary-ink hover:underline">{row.cliente}</Link>
                                                    : row.cliente}
                                            </span>
                                        )}
                                        {row.kind === 'error' && row.resultado && <span className="text-xs text-k-danger-ink">{row.resultado}</span>}
                                        {row.kind === 'pending' && <span className="text-xs text-k-muted">Procesamiento en curso</span>}
                                        {row.warnings.map((warning) => <span key={warning} className="text-xs font-semibold text-tone-orange-ink">{warning}</span>)}
                                    </span>
                                    <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-k-muted">{style.icon}{style.label}</span>
                                </li>
                            )
                        })}
                    </ul>
                )}

                {model.totalPages > 1 && (
                    <div className="flex items-center justify-end gap-1 text-sm">
                        <button
                            type="button"
                            aria-label="Página anterior"
                            disabled={model.page <= 1}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                            onClick={() => model.setPage(model.page - 1)}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </button>
                        {model.pages.map((number) => (
                            <button
                                key={number}
                                type="button"
                                aria-current={number === model.page ? 'page' : undefined}
                                className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium ${number === model.page
                                    ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                    : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                onClick={() => model.setPage(number)}
                            >
                                {number}
                            </button>
                        ))}
                        <button
                            type="button"
                            aria-label="Página siguiente"
                            disabled={model.page >= model.totalPages}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                            onClick={() => model.setPage(model.page + 1)}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </button>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default AutomationView
